'use client';

import {
  type ReactNode,
  useEffect,
  useState,
  useTransition,
} from 'react';

import Link from 'next/link';

import { formatDistanceToNow } from 'date-fns';
import { ar as arLocale } from 'date-fns/locale';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { PACKAGE_TYPE_LABEL_KEYS } from '@kit/bookings/constants';
import { formatLocaleDate, formatTimeToAmPm } from '@kit/shared/utils';
import { CurrencyIcon, useTenantCurrency } from '@kit/ui/currency-icon';
import { Trans } from '@kit/ui/trans';

import { DetailsDrawer } from '~/(tenant)/_components/details-drawer';
import { useNativeTokens } from '~/(tenant)/_components/native/_lib/use-native-tokens';
import {
  DirectionalArrow,
  EmptyState,
  Eyebrow,
  NativePageShell,
  Ornament,
} from '~/(tenant)/_components/native/_primitives';
import { NativeFilterChip } from '~/(tenant)/_components/native/_primitives/native-filter-chip';

import {
  PACKAGE_DESCRIPTION_OVERFLOW_CHARS,
  PACKAGE_FEATURES_PREVIEW_COUNT,
  formatPackageLabel,
  selectPackageCopy,
} from '../../book/_lib/format-package-label';
import type { PublicDiscountCode } from '../../book/_lib/public-discount-codes';
import { getBookingWalletBalanceAction } from '../../book/_lib/server/wallet-balance-action';
import type {
  ActivePackage,
  FamilyAccountActive,
  PackagesTab as Tab,
} from '../_lib/packages-initial-state';
import type { AvailablePackage } from '../_lib/server/available-packages.loader';
import {
  type PackageSummary,
  type PackagesPageItem,
  type UsageLogEntry,
  getPackageUsageLogsAction,
  getPackagesPageAction,
} from '../_lib/server/packages-page-actions';
import {
  type SessionBreakdown,
  computeSessionBreakdown,
  isWithinRenewalWindow,
  mergeSessionBreakdowns,
} from '../_lib/session-breakdown';
import { NativePackageOffers } from './native-package-offers';
import { PackagePurchaseFlow } from './package-purchase-flow';
import { RenewPackageDialog } from './renew-package-dialog';

interface NativePackagesPageProps {
  // Nullable: the page renders for anonymous visitors (the auth redirect was
  // removed so they can browse the Purchase catalog). Authenticated-only
  // tabs and server actions must gate on `hasContext` / userId presence.
  userId: string | null;
  accountId: string | null;
  availablePackages: AvailablePackage[];
  initialTab: Tab;
  initialPackageId?: string;
  promotionUnavailable?: boolean;
  summary: PackageSummary | null;
  activeFamilies: FamilyAccountActive[];
  // Public voucher codes that apply to each package, keyed by package id and
  // already narrowed server-side. Packages with no offer are absent from the
  // map. Mirrors the offers chip on the /book package cards.
  offersByPackage?: Record<string, PublicDiscountCode[]>;
  /** Every public code for the tenant — handed to the checkout dialog, which
   *  renders the applicable ones as one-tap buttons in its discount input. */
  publicDiscountCodes?: PublicDiscountCode[];
  /** Payment context for the in-place checkout dialog (see the page loader). */
  publishableKey?: string | null;
  provider?: string | null;
  tenantSlug?: string | null;
  userName?: string;
  userPhone?: string;
  // Member-club tenants relabel packages as memberships and don't surface a
  // credit ledger, so the AVAILABLE/USED/PACKAGES counts read as noise. The
  // server resolves this from business_type and passes it down.
  hideStats?: boolean;
}

const HISTORY_PAGE_SIZE = 10;

export function NativePackagesPage({
  userId,
  accountId,
  availablePackages,
  initialTab,
  initialPackageId,
  promotionUnavailable = false,
  summary,
  activeFamilies,
  offersByPackage,
  publicDiscountCodes = [],
  publishableKey,
  provider,
  tenantSlug,
  userName,
  userPhone,
  hideStats = false,
}: NativePackagesPageProps) {
  // Auth context drives which tabs are reachable. Anonymous visitors can
  // only see Purchase (the public catalog); Active and Expired require
  // userId because they call authenticated server actions.
  const hasContext = Boolean(userId && accountId);

  const [tab, setTab] = useState<Tab>(initialTab);

  // In-place purchase: which package's checkout dialog is open (null = none).
  const [purchasingPackage, setPurchasingPackage] =
    useState<AvailablePackage | null>(null);

  // Wallet tender, mirroring /book: balance + tenant flag are fetched once the
  // user is known. A failure leaves the wallet disabled rather than blocking
  // the catalog — the other tenders still work.
  const [wallet, setWallet] = useState({ enabled: false, balance: 0 });

  useEffect(() => {
    // Signed-out / no tenant: nothing to fetch. The state already starts
    // disabled, so returning early (rather than resetting synchronously) keeps
    // this effect free of the cascading-render setState pattern.
    if (!userId || !accountId) return;

    let cancelled = false;

    getBookingWalletBalanceAction({ accountId })
      .then((result) => {
        if (cancelled) return;
        setWallet({
          enabled: result.walletEnabled,
          balance: result.walletBalance,
        });
      })
      .catch(() => {
        // Non-fatal: the wallet tender simply stays hidden.
      });

    return () => {
      cancelled = true;
    };
  }, [userId, accountId]);


  return (
    <NativePageShell
      eyebrow={
        <Trans
          i18nKey="packages:nativeView.eyebrow"
          defaults="Membership · Packages"
        />
      }
      title={
        <Trans i18nKey="packages:nativeView.title" defaults="My packages" />
      }
    >
      {/* Stats strip — only meaningful for authenticated users on their own
          packages. Hidden for anonymous visitors (no summary) and on the
          Purchase tab, where the AVAILABLE/USED/PACKAGES counts are irrelevant
          and would just read as three zeros. Also hidden entirely for
          member-club tenants (`hideStats`), where the credit ledger is not
          part of the membership model. */}
      {!hideStats && hasContext && tab !== 'purchase' && (
        <StatsStrip summary={summary} />
      )}

      {/* Tabs — Active and Expired are hidden for anonymous visitors;
          they trigger authenticated server actions and would error
          without a session. */}
      <SegmentedTabs
        tab={tab}
        onChange={setTab}
        activeCount={activeFamilies.reduce((s, a) => s + a.packages.length, 0)}
        expiredCount={summary?.expired_count ?? 0}
        availableCount={availablePackages.length}
        showAuthTabs={hasContext}
      />

      {tab === 'active' && hasContext && (
        <ActiveTabContent
          families={activeFamilies}
          summary={summary}
          onSwitchToPurchase={() => setTab('purchase')}
          hasAvailable={availablePackages.length > 0}
        />
      )}
      {tab === 'expired' && hasContext && (
        <PackagesHistoryTab status="expired" />
      )}
      {tab === 'purchase' && (
        <PurchaseTabContent
          packages={availablePackages}
          initialPackageId={initialPackageId}
          promotionUnavailable={promotionUnavailable}
          offersByPackage={offersByPackage}
          onPurchase={setPurchasingPackage}
        />
      )}

      {/* Buying happens in place — see PackagePurchaseFlow for why the catalog
          no longer deep-links regular packages to /book. */}
      <PackagePurchaseFlow
        pkg={purchasingPackage}
        onClose={() => setPurchasingPackage(null)}
        userId={userId ?? undefined}
        userName={userName}
        userPhone={userPhone}
        tenantId={accountId ?? undefined}
        tenantSlug={tenantSlug}
        publishableKey={publishableKey}
        provider={provider}
        publicDiscountCodes={publicDiscountCodes}
        walletEnabled={wallet.enabled}
        walletBalance={wallet.balance}
        onWalletBalanceChange={(balance) =>
          setWallet((prev) => ({ ...prev, balance }))
        }
      />
    </NativePageShell>
  );
}

/* -------------------------------------------------------------------------- */
/* Header strip                                                               */
/* -------------------------------------------------------------------------- */

function StatsStrip({
  summary,
}: {
  summary: PackageSummary | null;
}) {
  const t = useNativeTokens();

  const cells = [
    {
      v: summary?.available_credits ?? 0,
      l: (
        <Trans
          i18nKey="packages:nativeView.statAvailable"
          defaults="Available"
        />
      ),
    },
    {
      v: summary?.used_credits ?? 0,
      l: <Trans i18nKey="packages:nativeView.statUsed" defaults="Used" />,
    },
    {
      v: summary?.total_count ?? 0,
      l: (
        <Trans i18nKey="packages:nativeView.statPackages" defaults="Packages" />
      ),
    },
  ];

  return (
    <div
      style={{
        margin: '0 0 28px',
        padding: '20px 4px',
        borderTop: `1px solid ${t.line}`,
        borderBottom: `1px solid ${t.line}`,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
      }}
    >
      {cells.map((cell, i) => (
        <div
          key={i}
          style={{
            textAlign: 'center',
            borderInlineStart: i ? `1px solid ${t.line}` : 'none',
          }}
        >
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 30,
              fontWeight: 300,
              color: t.foreground,
              lineHeight: 1,
              letterSpacing: '-0.01em',
            }}
          >
            {cell.v}
          </div>
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: t.muted,
              marginTop: 8,
            }}
          >
            {cell.l}
          </div>
        </div>
      ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Tabs                                                                       */
/* -------------------------------------------------------------------------- */

function SegmentedTabs({
  tab,
  onChange,
  activeCount,
  expiredCount,
  availableCount,
  showAuthTabs,
}: {
  tab: Tab;
  onChange: (next: Tab) => void;
  activeCount: number;
  expiredCount: number;
  availableCount: number;
  showAuthTabs: boolean;
}) {
  const t = useNativeTokens();
  return (
    <div
      role="tablist"
      style={{
        display: 'flex',
        gap: 24,
        flexWrap: 'wrap',
        borderBottom: `1px solid ${t.line}`,
        marginBottom: 20,
      }}
    >
      {showAuthTabs && (
        <>
          <SerifTab
            active={tab === 'active'}
            onClick={() => onChange('active')}
            count={activeCount}
          >
            <Trans i18nKey="packages:nativeView.tabActive" defaults="Active" />
          </SerifTab>
          <SerifTab
            active={tab === 'expired'}
            onClick={() => onChange('expired')}
            count={expiredCount}
          >
            <Trans
              i18nKey="packages:customerView.tabExpired"
              defaults="Expired"
            />
          </SerifTab>
        </>
      )}
      <SerifTab
        active={tab === 'purchase'}
        onClick={() => onChange('purchase')}
        count={availableCount}
      >
        <Trans i18nKey="packages:nativeView.tabPurchase" defaults="Purchase" />
      </SerifTab>
    </div>
  );
}

function SerifTab({
  active,
  onClick,
  children,
  count,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
  count: number;
}) {
  const t = useNativeTokens();
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        background: 'none',
        border: 'none',
        cursor: 'pointer',
        padding: '6px 0 12px',
        position: 'relative',
        color: active ? t.foreground : t.muted,
        display: 'flex',
        alignItems: 'baseline',
        gap: 8,
        marginBottom: -1,
        borderRadius: 0,
        borderBottom: active
          ? `2px solid ${t.foreground}`
          : '2px solid transparent',
      }}
    >
      <span
        style={{
          fontFamily: t.fontDisplay,
          fontSize: 22,
          fontWeight: active ? 500 : 400,
          letterSpacing: '-0.01em',
        }}
      >
        {children}
      </span>
      <span
        style={{
          fontFamily: t.fontMono,
          fontSize: 9,
          color: t.muted,
        }}
      >
        {count}
      </span>
    </button>
  );
}

/* -------------------------------------------------------------------------- */
/* Active tab                                                                 */
/* -------------------------------------------------------------------------- */

function ActiveTabContent({
  families,
  summary,
  onSwitchToPurchase,
  hasAvailable,
}: {
  families: FamilyAccountActive[];
  summary: PackageSummary | null;
  onSwitchToPurchase: () => void;
  hasAvailable: boolean;
}) {
  const t = useNativeTokens();

  const hasActive = families.some((f) => f.packages.length > 0);
  const totalEver = summary?.total_count ?? 0;
  const hasChildren = families.some((f) => f.is_child);

  if (!hasActive) {
    if (totalEver === 0) {
      return (
        <EmptyState
          compact
          mark="quiet"
          title={
            <Trans
              i18nKey="packages:nativeView.emptyTitle"
              defaults="No packages"
            />
          }
          cta={
            hasAvailable
              ? {
                  label: (
                    <Trans
                      i18nKey="packages:nativeView.viewPackages"
                      defaults="View packages"
                    />
                  ),
                  href: '#',
                }
              : undefined
          }
        />
      );
    }
    // Has only expired packages — direct user to the Expired tab.
    return (
      <p
        style={{
          margin: 0,
          padding: '32px 0',
          textAlign: 'center',
          color: t.muted,
          fontFamily: t.fontSans,
          fontSize: 13,
          fontStyle: 'italic',
        }}
      >
        <Trans
          i18nKey="packages:nativeView.noActive"
          defaults="No active packages."
        />
      </p>
    );
  }

  return (
    <div>
      <Eyebrow size="md" style={{ marginBottom: 14 }}>
        <Trans
          i18nKey="packages:nativeView.activeCredits"
          defaults="Active credits"
        />
      </Eyebrow>

      {!hasChildren ? (
        <ActivePackageList packages={families[0]?.packages ?? []} />
      ) : (
        <FamilyGroupedActive families={families} />
      )}

      {/* Inline upsell — borrowed from sample */}
      {hasAvailable && (
        <div
          style={{
            margin: '24px 0 0',
            padding: '24px 0',
            borderTop: `1px solid ${t.line}`,
            textAlign: 'center',
          }}
        >
          <Ornament />
          <p
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 19,
              fontStyle: 'italic',
              fontWeight: 300,
              color: t.foreground,
              marginTop: 16,
              marginBottom: 0,
              lineHeight: 1.3,
            }}
          >
            <Trans
              i18nKey="packages:nativeView.upsellTitle"
              defaults="Running low? Renew your credits."
            />
          </p>
          <button
            type="button"
            onClick={onSwitchToPurchase}
            style={{
              marginTop: 16,
              padding: '12px 24px',
              background: 'transparent',
              border: `1px solid ${t.foreground}`,
              color: t.foreground,
              borderRadius: t.radius,
              cursor: 'pointer',
              fontFamily: t.fontSans,
              fontSize: 11,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              fontWeight: 500,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <Trans
              i18nKey="packages:nativeView.viewPackages"
              defaults="View packages"
            />
            <DirectionalArrow size={12} strokeWidth={1.4} />
          </button>
        </div>
      )}
    </div>
  );
}

type DisplayUnit =
  | { kind: 'single'; pkg: ActivePackage }
  | { kind: 'bundle'; parent: ActivePackage; siblings: ActivePackage[] };

/**
 * Groups bundle parents and siblings by (user_id, bundle_id, purchased_at).
 * The fan-out trigger inserts siblings with the parent's exact purchased_at,
 * so this 3-tuple binds them together. Historical pre-bundle purchases of
 * the same sibling package carry a different purchased_at and stay
 * independent. Orphan siblings (no matching parent in this dataset) render
 * standalone so credits stay visible.
 */
function groupActivePackages(packages: ActivePackage[]): DisplayUnit[] {
  const parentByKey = new Map<string, ActivePackage>();
  for (const pkg of packages) {
    if (pkg.bundle_id && pkg.package.id === pkg.bundle_id) {
      parentByKey.set(
        `${pkg.user_id}:${pkg.bundle_id}:${pkg.purchased_at}`,
        pkg,
      );
    }
  }

  const result: DisplayUnit[] = [];
  const emitted = new Set<string>();

  for (const pkg of packages) {
    const isParent = pkg.bundle_id && pkg.package.id === pkg.bundle_id;
    const isSibling = pkg.bundle_id && !isParent;
    const key = `${pkg.user_id}:${pkg.bundle_id}:${pkg.purchased_at}`;

    if (isParent) {
      if (emitted.has(key)) continue;
      emitted.add(key);
      const sibs = packages.filter(
        (p) =>
          p.bundle_id === pkg.bundle_id &&
          p.package.id !== pkg.bundle_id &&
          p.user_id === pkg.user_id &&
          p.purchased_at === pkg.purchased_at,
      );
      result.push({ kind: 'bundle', parent: pkg, siblings: sibs });
      continue;
    }

    if (isSibling) {
      // Sibling whose parent is in this dataset — folded in by the parent above.
      if (parentByKey.has(key)) continue;
      // Orphan sibling — render standalone.
      result.push({ kind: 'single', pkg });
      continue;
    }

    result.push({ kind: 'single', pkg });
  }

  return result;
}

/**
 * A display unit is still LIVE if the customer has anything left to come:
 * credits they can still book, or sessions already booked in the future.
 *
 * Remaining credits alone would file a fixed-slot package (every credit spent
 * at purchase, all sessions ahead) under "Fully used" on the day it's bought.
 */
function unitIsLive(unit: DisplayUnit, now: Date): boolean {
  const rows = unit.kind === 'single' ? [unit.pkg] : unit.siblings;

  return rows.some((row) => {
    if (row.remaining_booking_count > 0) return true;

    const breakdown = computeSessionBreakdown(
      row.usage_logs,
      row.remaining_booking_count,
      row.original_booking_count,
      now,
    );

    return !breakdown.isPartial && breakdown.upcoming > 0;
  });
}

function ActivePackageList({ packages }: { packages: ActivePackage[] }) {
  const t = useNativeTokens();
  const [now] = useState(() => new Date());
  const units = groupActivePackages(packages);
  const usable = units.filter((u) => unitIsLive(u, now));
  const used = units.filter((u) => !unitIsLive(u, now));

  return (
    <div>
      {usable.map((unit) =>
        unit.kind === 'single' ? (
          <NativeActivePackageCard key={unit.pkg.id} pkg={unit.pkg} />
        ) : (
          <NativeBundleCard
            key={unit.parent.id}
            parent={unit.parent}
            siblings={unit.siblings}
          />
        ),
      )}

      {used.length > 0 && (
        <div
          style={{
            marginTop: usable.length > 0 ? 24 : 0,
            paddingTop: usable.length > 0 ? 20 : 0,
            borderTop: usable.length > 0 ? `1px solid ${t.line}` : 'none',
          }}
        >
          <Eyebrow size="sm" style={{ marginBottom: 12 }}>
            <Trans
              i18nKey="packages:nativeView.fullyUsedHeading"
              defaults="Fully used"
            />
          </Eyebrow>
          {used.map((unit) =>
            unit.kind === 'single' ? (
              <NativeActivePackageCard key={unit.pkg.id} pkg={unit.pkg} />
            ) : (
              <NativeBundleCard
                key={unit.parent.id}
                parent={unit.parent}
                siblings={unit.siblings}
              />
            ),
          )}
        </div>
      )}
    </div>
  );
}

function FamilyGroupedActive({
  families,
}: {
  families: FamilyAccountActive[];
}) {
  const t = useNativeTokens();
  const { t: tr } = useTranslation('packages');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {families
        .filter((f) => f.packages.length > 0)
        .map((family, idx) => (
          <section
            key={family.account_id}
            style={{ borderTop: idx === 0 ? 'none' : `1px solid ${t.line}` }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'baseline',
                gap: 10,
                padding: '18px 0 14px',
              }}
            >
              <span
                style={{
                  fontFamily: t.fontDisplay,
                  fontSize: 18,
                  fontWeight: 400,
                  color: t.foreground,
                  letterSpacing: '-0.01em',
                }}
              >
                {family.is_child
                  ? family.account_name
                  : tr('customerView.myPackages')}
              </span>
              {family.is_child && (
                <span
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: t.muted,
                  }}
                >
                  <Trans i18nKey="packages:customerView.child" />
                </span>
              )}
              <span
                style={{
                  marginInlineStart: 'auto',
                  fontFamily: t.fontMono,
                  fontSize: 11,
                  letterSpacing: '0.06em',
                  color: t.muted,
                }}
              >
                {family.packages.length}
              </span>
            </div>
            <ActivePackageList packages={family.packages} />
          </section>
        ))}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Bundle card — parent + dropdown of sibling rows                            */
/* -------------------------------------------------------------------------- */

function NativeBundleCard({
  parent,
  siblings,
}: {
  parent: ActivePackage;
  siblings: ActivePackage[];
}) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation('packages');
  const [expanded, setExpanded] = useState(false);
  const [now] = useState(() => new Date());

  // Aggregated credits across all siblings (parent has 0 of its own).
  const totalRemaining = siblings.reduce(
    (s, x) => s + x.remaining_booking_count,
    0,
  );
  const totalOriginal = siblings.reduce(
    (s, x) => s + x.original_booking_count,
    0,
  );
  const usedCount = totalOriginal - totalRemaining;
  const usagePercent =
    totalOriginal > 0
      ? Math.max(0, Math.min(100, (usedCount / totalOriginal) * 100))
      : 0;
  const accent = parent.package.color || t.foreground;

  // Same attended/upcoming split as the single card, summed across siblings
  // (the parent row carries no credits of its own).
  const breakdown = mergeSessionBreakdowns(
    siblings.map((sib) =>
      computeSessionBreakdown(
        sib.usage_logs,
        sib.remaining_booking_count,
        sib.original_booking_count,
        now,
      ),
    ),
  );

  const sessionsLeft = breakdown.upcoming + breakdown.available;
  const showBreakdown = !breakdown.isPartial;

  const expiresInDays = parent.expires_at
    ? Math.ceil(
        (new Date(parent.expires_at).getTime() - now.getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;
  const showExpiryWarning =
    expiresInDays !== null && expiresInDays > 0 && expiresInDays <= 30;

  return (
    <div
      style={{
        padding: '22px 22px 20px',
        background: t.background,
        border: `1px solid ${t.line}`,
        borderRadius: t.radius,
        marginBottom: 14,
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 22,
          bottom: 22,
          insetInlineStart: 0,
          width: 2,
          background: accent,
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 14,
          marginBottom: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow size="sm" style={{ marginBottom: 6 }}>
            <Trans i18nKey="packages:nativeView.bundle" defaults="Bundle" />
          </Eyebrow>
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 22,
              fontWeight: 400,
              color: t.foreground,
              lineHeight: 1.1,
            }}
          >
            {parent.package.name}
          </div>
        </div>
        {/* A bundle is frozen when its parent is (siblings are frozen with it). */}
        <StatusPill
          status={
            parent.status === 'frozen'
              ? 'frozen'
              : totalRemaining > 0
                ? 'active'
                : showBreakdown && breakdown.upcoming > 0
                  ? 'booked'
                  : 'used'
          }
        />
      </div>

      {/* Aggregate credits */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          marginTop: 18,
        }}
      >
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 44,
            fontWeight: 300,
            color: t.foreground,
            lineHeight: 0.9,
            letterSpacing: '-0.02em',
          }}
        >
          {showBreakdown ? sessionsLeft : totalRemaining}
        </div>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 22,
            fontWeight: 300,
            color: t.muted,
            lineHeight: 1,
          }}
        >
          / {totalOriginal}
        </div>
        <div
          style={{
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: t.muted,
            marginInlineStart: 'auto',
          }}
        >
          {showBreakdown ? (
            <Trans
              i18nKey="packages:nativeView.sessionsLeftSuffix"
              defaults="left"
            />
          ) : (
            <>
              {usedCount}{' '}
              <Trans i18nKey="packages:nativeView.usedSuffix" defaults="used" />
            </>
          )}
        </div>
      </div>

      {showBreakdown ? (
        <SessionBreakdownStrip
          breakdown={breakdown}
          segments={toBreakdownSegments(breakdown)}
        />
      ) : (
        <div
          style={{
            height: 1,
            background: t.line,
            marginTop: 14,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -1,
              insetInlineStart: 0,
              height: 3,
              width: `${usagePercent}%`,
              background: t.foreground,
            }}
          />
        </div>
      )}

      {/* Meta row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 16,
          marginTop: 16,
          fontFamily: t.fontSans,
          fontSize: 11,
          color: t.muted,
        }}
      >
        {parent.expires_at && (
          <span>
            <Trans
              i18nKey="packages:customerView.expiresOn"
              values={{
                date: formatLocaleDate(parent.expires_at, i18n.language, {
                  year: undefined,
                }),
              }}
            />
          </span>
        )}
        {parent.purchase_price > 0 && (
          <span
            style={{
              marginInlineStart: 'auto',
              fontFamily: t.fontDisplay,
              fontSize: 13,
              fontWeight: 400,
              color: t.foreground,
              letterSpacing: '0.01em',
            }}
          >
            <SARSymbol size={10} />
            {parent.purchase_price.toLocaleString()}
          </span>
        )}
      </div>

      {/* Dropdown toggle */}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          marginTop: 14,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          background: 'transparent',
          border: 'none',
          padding: 0,
          cursor: 'pointer',
          fontFamily: t.fontMono,
          fontSize: 10,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: t.muted,
        }}
        aria-expanded={expanded}
      >
        <ChevronDown
          size={12}
          style={{
            transition: 'transform 150ms',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        />
        {expanded ? (
          <Trans
            i18nKey="packages:nativeView.hideBreakdown"
            defaults="Hide breakdown"
          />
        ) : (
          <Trans
            i18nKey="packages:nativeView.showBreakdown"
            values={{ count: siblings.length }}
            defaults="{{count}} included · show breakdown"
          />
        )}
      </button>

      {expanded && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${t.line}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {siblings.map((sib) => (
            <div
              key={sib.id}
              style={{
                display: 'flex',
                alignItems: 'baseline',
                justifyContent: 'space-between',
                gap: 10,
              }}
            >
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontFamily: t.fontSans,
                    fontSize: 13,
                    color: t.foreground,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {sib.package.name}
                </div>
                <div
                  style={{
                    fontFamily: t.fontMono,
                    fontSize: 9,
                    letterSpacing: '0.16em',
                    textTransform: 'uppercase',
                    color: t.muted,
                    marginTop: 2,
                  }}
                >
                  {tr('customerView.minSlots', {
                    duration: sib.package.slot_duration,
                  })}
                </div>
              </div>
              <div
                style={{
                  fontFamily: t.fontDisplay,
                  fontSize: 18,
                  fontWeight: 300,
                  color: t.foreground,
                  letterSpacing: '-0.01em',
                }}
              >
                {sib.remaining_booking_count}
                <span style={{ color: t.muted, fontSize: 13 }}>
                  {' '}
                  / {sib.original_booking_count}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showExpiryWarning && parent.expires_at && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: `1px solid ${t.line}`,
            fontFamily: t.fontSans,
            fontSize: 11,
            color: t.muted,
          }}
        >
          <Trans
            i18nKey="packages:nativeView.expiringSoon"
            values={{
              relative: formatDistanceToNow(new Date(parent.expires_at), {
                addSuffix: true,
                locale: i18n.language === 'ar' ? arLocale : undefined,
              }),
            }}
          />
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Session breakdown — attended / upcoming / available                        */
/* -------------------------------------------------------------------------- */

/**
 * Renders the three session states under the hero number. Shared by the single
 * and bundle cards so both tell the same story.
 *
 * Only the states that apply are shown: a package with nothing attended yet
 * doesn't need a "0 attended" chip, and the whole strip is skipped when the
 * ledger couldn't explain the spent credits (isPartial) — a wrong split is
 * worse than none.
 */
function SessionBreakdownStrip({
  breakdown,
  segments,
}: {
  breakdown: SessionBreakdown;
  /** Progress segments — attended solid, upcoming hatched. */
  segments: { attendedPercent: number; upcomingPercent: number };
}) {
  const t = useNativeTokens();
  const { i18n } = useTranslation('packages');

  const parts: ReactNode[] = [];

  if (breakdown.attended > 0) {
    parts.push(
      <span key="attended" style={{ color: t.foreground }}>
        <Trans
          i18nKey="packages:nativeView.sessionsAttended"
          values={{ count: breakdown.attended }}
          defaults="{{count}} attended"
        />
      </span>,
    );
  }

  if (breakdown.upcoming > 0) {
    parts.push(
      <span key="upcoming" style={{ color: t.foreground }}>
        <Trans
          i18nKey="packages:nativeView.sessionsUpcoming"
          values={{ count: breakdown.upcoming }}
          defaults="{{count}} upcoming"
        />
      </span>,
    );
  }

  parts.push(
    <span key="available">
      <Trans
        i18nKey="packages:nativeView.sessionsAvailable"
        values={{ count: breakdown.available }}
        defaults="{{count}} available to book"
      />
    </span>,
  );

  return (
    <>
      {/* Segmented progress: attended is solid, upcoming is a lighter band so
          the customer can see time already committed vs already spent. */}
      <div
        style={{
          height: 1,
          background: t.line,
          marginTop: 14,
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: -1,
            insetInlineStart: 0,
            height: 3,
            width: `${segments.attendedPercent}%`,
            background: t.foreground,
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: -1,
            insetInlineStart: `${segments.attendedPercent}%`,
            height: 3,
            width: `${segments.upcomingPercent}%`,
            background: t.foreground,
            opacity: 0.35,
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          gap: 10,
          marginTop: 10,
          fontFamily: t.fontMono,
          fontSize: 9,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: t.muted,
        }}
      >
        {parts.map((part, index) => (
          <span key={index} style={{ display: 'inline-flex', gap: 10 }}>
            {index > 0 && <span aria-hidden="true">·</span>}
            {part}
          </span>
        ))}
      </div>

      {breakdown.nextSessionDate && (
        <div
          style={{
            marginTop: 10,
            fontFamily: t.fontSans,
            fontSize: 11,
            color: t.muted,
          }}
        >
          <Trans
            i18nKey="packages:nativeView.nextSession"
            values={{
              date: formatLocaleDate(breakdown.nextSessionDate, i18n.language, {
                year: undefined,
              }),
            }}
            defaults="Next session {{date}}"
          />
        </div>
      )}
    </>
  );
}

/**
 * Percentages for the segmented bar. Guards a zero total so a malformed
 * package can't produce NaN widths.
 */
function toBreakdownSegments(breakdown: SessionBreakdown) {
  const denominator =
    breakdown.attended + breakdown.upcoming + breakdown.available;

  if (denominator <= 0) {
    return { attendedPercent: 0, upcomingPercent: 0 };
  }

  return {
    attendedPercent: (breakdown.attended / denominator) * 100,
    upcomingPercent: (breakdown.upcoming / denominator) * 100,
  };
}

/* -------------------------------------------------------------------------- */
/* Active package card — editorial                                            */
/* -------------------------------------------------------------------------- */

function NativeActivePackageCard({ pkg }: { pkg: ActivePackage }) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation('packages');
  const [isRenewOpen, setIsRenewOpen] = useState(false);
  const [now] = useState(() => new Date());

  const usedCount = pkg.original_booking_count - pkg.remaining_booking_count;
  const usagePercent = Math.max(
    0,
    Math.min(100, (usedCount / pkg.original_booking_count) * 100),
  );
  const accent = pkg.package.color || t.foreground;

  // Split spent credits into sessions already attended vs still to come.
  // Credits leave remaining_booking_count at BOOKING time (fixed-slot packages
  // spend all of them at purchase), so `used` on its own reads as "nothing
  // left" to a customer whose sessions are all still ahead of them.
  const breakdown = computeSessionBreakdown(
    pkg.usage_logs,
    pkg.remaining_booking_count,
    pkg.original_booking_count,
    now,
  );

  // Sessions the customer still has coming — the number they actually care
  // about. Falls back to raw remaining credits when the ledger is incomplete.
  const sessionsLeft = breakdown.upcoming + breakdown.available;
  const showBreakdown = !breakdown.isPartial;

  const expiresInDays = pkg.expires_at
    ? Math.ceil(
        (new Date(pkg.expires_at).getTime() - now.getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;
  const showExpiryWarning =
    expiresInDays !== null && expiresInDays > 0 && expiresInDays <= 30;

  // Renewal stays open until the LAST session starts, then closes — a package
  // down to its final hours is no longer renewable (isWithinRenewalWindow).
  const canRenew =
    pkg.package.is_fixed_slot &&
    pkg.fixed_slot !== null &&
    isWithinRenewalWindow(pkg.usage_logs, now);

  // Not-started gate mirror (isPackageStarted, BOOK-BY only): a book-by
  // package whose starts_at is still in the future (chosen start date) is live
  // but not yet spendable — it stays in the Active tab with a "Starts <date>"
  // pill. Attend-by packages allow advance booking before their window opens,
  // so they render as Active; their window dates are shown on the card.
  const notStartedYet =
    pkg.status === 'active' &&
    !pkg.require_attendance_within_validity &&
    !!pkg.starts_at &&
    new Date(pkg.starts_at).getTime() > now.getTime();

  // Attend-by packages can carry a customer-chosen start (sessions must fall
  // on/after it). They stay Active — advance booking is allowed — so surface
  // the date as a plain "Starts" line next to the expiry instead of a pill.
  // Same status gate as notStartedYet: a frozen row shows its Frozen pill only.
  const attendByFutureStart =
    pkg.status === 'active' &&
    pkg.require_attendance_within_validity === true &&
    !!pkg.starts_at &&
    new Date(pkg.starts_at).getTime() > now.getTime();

  const kindLabel = formatPackageLabel(
    tr,
    pkg.package.package_type,
    pkg.original_booking_count,
  );

  return (
    <div
      style={{
        padding: '22px 22px 20px',
        background: t.background,
        border: `1px solid ${t.line}`,
        borderRadius: t.radius,
        marginBottom: 14,
        position: 'relative',
      }}
    >
      {/* Hairline accent on inline-start edge */}
      <div
        style={{
          position: 'absolute',
          top: 22,
          bottom: 22,
          insetInlineStart: 0,
          width: 2,
          background: accent,
        }}
      />

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 14,
          marginBottom: 4,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow size="sm" style={{ marginBottom: 6 }}>
            {kindLabel}
          </Eyebrow>
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 22,
              fontWeight: 400,
              color: t.foreground,
              lineHeight: 1.1,
            }}
          >
            {pkg.package.name}
          </div>
        </div>
        {/* Status is checked BEFORE the credit count: a frozen package still has
            credits, and showing it as "Active" would tell the customer it's
            bookable when every redemption path will refuse it. Same for a
            not-yet-started BOOK-BY package — its credits open on the start
            date. Attend-by packages are bookable in advance, so they never
            show the scheduled state. */}
        {/* 'used' would be a lie while sessions are still upcoming: a
            fixed-slot package spends every credit at purchase, so it hits
            0 remaining before the customer has attended anything. Booked-out
            says what's actually true — nothing left to book, sessions ahead. */}
        <StatusPill
          status={
            pkg.status === 'frozen'
              ? 'frozen'
              : notStartedYet
                ? 'scheduled'
                : pkg.remaining_booking_count > 0
                  ? 'active'
                  : showBreakdown && breakdown.upcoming > 0
                    ? 'booked'
                    : 'used'
          }
          date={
            notStartedYet && pkg.starts_at
              ? formatLocaleDate(pkg.starts_at, i18n.language, {
                  year: undefined,
                })
              : undefined
          }
        />
      </div>

      {/* Hero: sessions the customer still has, not credits left to book.
          Those differ the moment a session is booked in advance. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 12,
          marginTop: 18,
        }}
      >
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 44,
            fontWeight: 300,
            color: t.foreground,
            lineHeight: 0.9,
            letterSpacing: '-0.02em',
          }}
        >
          {showBreakdown ? sessionsLeft : pkg.remaining_booking_count}
        </div>
        <div
          style={{
            fontFamily: t.fontDisplay,
            fontSize: 22,
            fontWeight: 300,
            color: t.muted,
            lineHeight: 1,
          }}
        >
          / {pkg.original_booking_count}
        </div>
        <div
          style={{
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: t.muted,
            marginInlineStart: 'auto',
          }}
        >
          {showBreakdown ? (
            <Trans
              i18nKey="packages:nativeView.sessionsLeftSuffix"
              defaults="left"
            />
          ) : (
            <>
              {usedCount}{' '}
              <Trans i18nKey="packages:nativeView.usedSuffix" defaults="used" />
            </>
          )}
        </div>
      </div>

      {showBreakdown ? (
        <SessionBreakdownStrip
          breakdown={breakdown}
          segments={toBreakdownSegments(breakdown)}
        />
      ) : (
        /* Ledger incomplete (truncated logs or a hard-deleted booking) —
           degrade to the plain usage bar rather than show a wrong split. */
        <div
          style={{
            height: 1,
            background: t.line,
            marginTop: 14,
            position: 'relative',
          }}
        >
          <div
            style={{
              position: 'absolute',
              top: -1,
              insetInlineStart: 0,
              height: 3,
              width: `${usagePercent}%`,
              background: t.foreground,
            }}
          />
        </div>
      )}

      {/* Meta row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 16,
          marginTop: 16,
          fontFamily: t.fontSans,
          fontSize: 11,
          color: t.muted,
        }}
      >
        <span>
          {tr('customerView.minSlots', { duration: pkg.package.slot_duration })}
        </span>
        {attendByFutureStart && pkg.starts_at && (
          <span>
            <Trans
              i18nKey="packages:customerView.startsOn"
              values={{
                date: formatLocaleDate(pkg.starts_at, i18n.language, {
                  year: undefined,
                }),
              }}
            />
          </span>
        )}
        {pkg.expires_at && (
          <span>
            <Trans
              i18nKey="packages:customerView.expiresOn"
              values={{
                date: formatLocaleDate(pkg.expires_at, i18n.language, {
                  year: undefined,
                }),
              }}
            />
          </span>
        )}
        {pkg.purchase_price > 0 && (
          <span
            style={{
              marginInlineStart: 'auto',
              fontFamily: t.fontDisplay,
              fontSize: 13,
              fontWeight: 400,
              color: t.foreground,
              letterSpacing: '0.01em',
            }}
          >
            <SARSymbol size={10} />
            {pkg.purchase_price.toLocaleString()}
          </span>
        )}
      </div>

      {showExpiryWarning && pkg.expires_at && (
        <div
          style={{
            marginTop: 14,
            padding: '10px 12px',
            borderRadius: t.radius,
            background: t.line,
            fontFamily: t.fontMono,
            fontSize: 9,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: t.foreground,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span
            style={{
              width: 4,
              height: 4,
              borderRadius: 4,
              background: accent,
            }}
          />
          <Trans
            i18nKey="packages:customerView.expiryWarning"
            values={{
              time: formatDistanceToNow(new Date(pkg.expires_at), {
                locale: i18n.language === 'ar' ? arLocale : undefined,
              }),
            }}
          />
        </div>
      )}

      {/* Actions — hidden entirely for SAR 0 packages with nothing to renew */}
      {(canRenew || pkg.purchase_price > 0) && (
        <div
          style={{
            display: 'flex',
            gap: 10,
            marginTop: 16,
            paddingTop: 16,
            borderTop: `1px solid ${t.line}`,
          }}
        >
          {canRenew && (
            <button
              type="button"
              onClick={() => setIsRenewOpen(true)}
              style={{
                padding: '10px 18px',
                background: t.accent,
                color: t.background,
                border: 'none',
                borderRadius: t.radius,
                cursor: 'pointer',
                fontFamily: t.fontSans,
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 500,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <RefreshCw size={11} strokeWidth={1.6} />
              <Trans i18nKey="packages:renew.button" defaults="Renew" />
            </button>
          )}
          {pkg.purchase_price > 0 && (
            <Link
              href={`/payment/package/${pkg.id}`}
              style={{
                marginInlineStart: canRenew ? 0 : 'auto',
                padding: '10px 18px',
                background: 'transparent',
                color: t.foreground,
                border: `1px solid ${t.foreground}`,
                borderRadius: t.radius,
                fontFamily: t.fontSans,
                fontSize: 11,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 500,
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              <Trans i18nKey="packages:customerView.viewPayment" />
            </Link>
          )}
        </div>
      )}

      {canRenew && (
        <RenewPackageDialog
          isOpen={isRenewOpen}
          onClose={() => setIsRenewOpen(false)}
          userPackageId={pkg.id}
          packageName={pkg.package.name}
          renewalPrice={pkg.renewal_price}
          slotDuration={pkg.package.slot_duration}
          fixedSlot={pkg.fixed_slot}
        />
      )}
    </div>
  );
}

function StatusPill({
  status,
  date,
}: {
  /** 'booked' = no credits left to book, but sessions still upcoming. */
  status: 'active' | 'used' | 'frozen' | 'scheduled' | 'booked';
  /** Localized start date — required copy for the 'scheduled' state. */
  date?: string;
}) {
  const t = useNativeTokens();
  const { t: tr } = useTranslation('packages');
  return (
    <span
      style={{
        padding: '4px 9px',
        borderRadius: 999,
        background: `${t.foreground}14`,
        fontFamily: t.fontMono,
        fontSize: 8,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        color: t.foreground,
        flexShrink: 0,
      }}
    >
      {status === 'frozen'
        ? tr('nativeView.statusFrozen', { defaultValue: 'Frozen' })
        : status === 'scheduled'
          ? tr('nativeView.statusStarts', {
              defaultValue: 'Starts {{date}}',
              date,
            })
          : status === 'booked'
            ? tr('nativeView.statusBooked', { defaultValue: 'Booked' })
            : status === 'active'
              ? tr('nativeView.statusActive')
              : tr('nativeView.statusUsed')}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Expired tab — paginated list                                               */
/* -------------------------------------------------------------------------- */

function PackagesHistoryTab({ status }: { status: 'used' | 'expired' }) {
  const t = useNativeTokens();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{
    items: PackagesPageItem[];
    totalCount: number;
  } | null>(null);
  const [isPending, startTransition] = useTransition();

  useEffect(() => {
    startTransition(async () => {
      const res = await getPackagesPageAction({
        status,
        page,
        pageSize: HISTORY_PAGE_SIZE,
      });
      if (res.success && res.data) {
        setData({ items: res.data.items, totalCount: res.data.totalCount });
      } else {
        setData({ items: [], totalCount: 0 });
      }
    });
  }, [status, page]);

  const loading = isPending;
  const totalPages = data
    ? Math.max(1, Math.ceil(data.totalCount / HISTORY_PAGE_SIZE))
    : 1;

  if (loading && !data) {
    return <HistorySkeleton />;
  }

  if (!data || data.items.length === 0) {
    return (
      <p
        style={{
          margin: 0,
          padding: '32px 0',
          textAlign: 'center',
          color: t.muted,
          fontFamily: t.fontSans,
          fontSize: 13,
          fontStyle: 'italic',
        }}
      >
        <Trans
          i18nKey={
            status === 'used'
              ? 'packages:customerView.noUsedPackages'
              : 'packages:customerView.noExpiredPackages'
          }
        />
      </p>
    );
  }

  return (
    <div>
      <div style={{ opacity: loading ? 0.6 : 1, transition: 'opacity 200ms' }}>
        {data.items.map((item, idx) => (
          <NativeCompactRow
            key={item.id}
            item={item}
            isLast={idx === data.items.length - 1}
          />
        ))}
      </div>

      {totalPages > 1 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            paddingTop: 16,
            marginTop: 12,
            borderTop: `1px solid ${t.line}`,
          }}
        >
          <span
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.muted,
            }}
          >
            <Trans
              i18nKey="packages:customerView.page"
              values={{ current: page, total: totalPages }}
            />
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            <PageButton
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
            >
              <Trans i18nKey="packages:customerView.previousPage" />
            </PageButton>
            <PageButton
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages || loading}
            >
              <Trans i18nKey="packages:customerView.nextPage" />
            </PageButton>
          </div>
        </div>
      )}
    </div>
  );
}

function PageButton({
  onClick,
  disabled,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  children: ReactNode;
}) {
  const t = useNativeTokens();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 14px',
        background: 'transparent',
        color: disabled ? t.muted : t.foreground,
        border: `1px solid ${disabled ? t.line : t.foreground}`,
        borderRadius: t.radius,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontFamily: t.fontSans,
        fontSize: 10,
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        fontWeight: 500,
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}

function NativeCompactRow({
  item,
  isLast,
}: {
  item: PackagesPageItem;
  isLast: boolean;
}) {
  const t = useNativeTokens();
  const { i18n } = useTranslation('packages');
  const [open, setOpen] = useState(false);
  const usedCount = item.original_booking_count - item.remaining_booking_count;
  const accent = item.package.color || t.muted;
  const isExpired =
    item.status === 'expired' ||
    (item.expires_at && new Date(item.expires_at) < new Date());

  return (
    <div style={{ borderBottom: isLast ? 'none' : `1px solid ${t.line}` }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        style={{
          width: '100%',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          padding: '14px 0',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          textAlign: 'start',
          color: t.foreground,
          opacity: 0.75,
        }}
      >
        <span
          style={{
            width: 2,
            height: 28,
            background: accent,
            flexShrink: 0,
            borderRadius: 1,
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 16,
              fontWeight: 400,
              color: t.foreground,
              letterSpacing: '-0.005em',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {item.package.name}
            {item.is_child && (
              <span
                style={{
                  marginInlineStart: 8,
                  fontFamily: t.fontMono,
                  fontSize: 8,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: t.muted,
                }}
              >
                · {item.account_name}
              </span>
            )}
          </div>
          <div
            style={{
              marginTop: 4,
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.muted,
            }}
          >
            {usedCount}/{item.original_booking_count}{' '}
            <Trans i18nKey="packages:nativeView.usedSuffix" defaults="used" />
            {item.expires_at && (
              <>
                {' · '}
                {isExpired ? (
                  <Trans
                    i18nKey="packages:customerView.expiredOn"
                    values={{
                      date: formatLocaleDate(item.expires_at, i18n.language, {
                        year: undefined,
                      }),
                    }}
                  />
                ) : (
                  <Trans
                    i18nKey="packages:customerView.expiresOn"
                    values={{
                      date: formatLocaleDate(item.expires_at, i18n.language, {
                        year: undefined,
                      }),
                    }}
                  />
                )}
              </>
            )}
          </div>
        </div>
        {item.purchase_price > 0 && (
          <span
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 13,
              color: t.muted,
              flexShrink: 0,
            }}
          >
            <SARSymbol size={9} />
            {item.purchase_price.toLocaleString()}
          </span>
        )}
        <ChevronDown
          size={14}
          strokeWidth={1.5}
          style={{
            color: t.muted,
            flexShrink: 0,
            transition: 'transform 200ms',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        />
      </button>

      {open && (
        <div style={{ paddingBottom: 16 }}>
          <LazyUsageHistory userPackageId={item.id} />
          {item.purchase_price > 0 && (
            <Link
              href={`/payment/package/${item.id}`}
              style={{
                marginTop: 12,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                padding: '8px 14px',
                border: `1px solid ${t.line}`,
                borderRadius: t.radius,
                color: t.muted,
                textDecoration: 'none',
                fontFamily: t.fontSans,
                fontSize: 10,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}
            >
              <Trans i18nKey="packages:customerView.viewPayment" />
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function LazyUsageHistory({ userPackageId }: { userPackageId: string }) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation('packages');
  const [logs, setLogs] = useState<UsageLogEntry[] | null>(null);
  const [, startTransition] = useTransition();

  useEffect(() => {
    if (logs !== null) return;
    startTransition(async () => {
      const res = await getPackageUsageLogsAction({ userPackageId });
      setLogs(res.success ? (res.data ?? []) : []);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPackageId]);

  if (logs === null) {
    return (
      <p
        style={{
          margin: 0,
          padding: '8px 0',
          fontFamily: t.fontMono,
          fontSize: 9,
          letterSpacing: '0.18em',
          textTransform: 'uppercase',
          color: t.muted,
        }}
      >
        <Trans i18nKey="packages:customerView.loadingHistory" />
      </p>
    );
  }

  const deductions = logs.filter((l) => l.credits_used > 0);

  if (deductions.length === 0) {
    return (
      <p
        style={{
          margin: 0,
          padding: '8px 0',
          fontFamily: t.fontSans,
          fontSize: 12,
          fontStyle: 'italic',
          color: t.muted,
        }}
      >
        <Trans i18nKey="packages:customerView.noCreditUsage" />
      </p>
    );
  }

  return (
    <div
      style={{
        borderTop: `1px solid ${t.line}`,
        paddingTop: 8,
      }}
    >
      {deductions.map((log, i) => {
        const hasBooking = log.booking_date !== null;
        const dateStr = hasBooking
          ? formatLocaleDate(log.booking_date!, i18n.language, {
              year: undefined,
            })
          : formatLocaleDate(log.used_at, i18n.language, { year: undefined });
        const timeStr =
          log.start_time && log.end_time
            ? `${formatTimeToAmPm(log.start_time, i18n.language)} – ${formatTimeToAmPm(log.end_time, i18n.language)}`
            : null;

        let label: string;
        if (!hasBooking) {
          label = tr('customerView.manualAdjustment');
        } else if (log.is_class) {
          label = log.activity_name || tr('customerView.classSession');
        } else {
          label = log.activity_name || tr('customerView.courtBooking');
        }

        return (
          <div
            key={i}
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 12,
              padding: '8px 0',
              fontFamily: t.fontSans,
              fontSize: 12,
              color: t.foreground,
            }}
          >
            <span
              style={{
                fontFamily: t.fontMono,
                fontSize: 9,
                letterSpacing: '0.16em',
                textTransform: 'uppercase',
                color: t.muted,
                width: 56,
                flexShrink: 0,
              }}
            >
              {dateStr}
            </span>
            <span
              style={{
                flex: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {label}
            </span>
            {timeStr && (
              <span
                style={{
                  fontFamily: t.fontMono,
                  fontSize: 9,
                  letterSpacing: '0.1em',
                  color: t.muted,
                  flexShrink: 0,
                }}
              >
                {timeStr}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Purchase tab                                                               */
/* -------------------------------------------------------------------------- */

function PurchaseTabContent({
  packages,
  initialPackageId,
  promotionUnavailable,
  offersByPackage,
  onPurchase,
}: {
  packages: AvailablePackage[];
  initialPackageId?: string;
  promotionUnavailable?: boolean;
  offersByPackage?: Record<string, PublicDiscountCode[]>;
  onPurchase?: (pkg: AvailablePackage) => void;
}) {
  const t = useNativeTokens();
  const { t: tr } = useTranslation('bookings');
  const [selectedType, setSelectedType] = useState<string>('all');
  const [focusedPackageId, setFocusedPackageId] = useState(initialPackageId);

  if (packages.length === 0) {
    return (
      <EmptyState
        mark="quiet"
        eyebrow={
          <Trans
            i18nKey="packages:nativeView.purchaseEmptyEyebrow"
            defaults="Catalog at rest"
          />
        }
        title={
          <Trans
            i18nKey="packages:nativeView.purchaseEmptyTitle"
            defaults="No packages"
          />
        }
        emphasis={
          <Trans
            i18nKey="packages:nativeView.purchaseEmptyEmphasis"
            defaults="available."
          />
        }
        body={
          <Trans
            i18nKey="packages:nativeView.purchaseEmptyBody"
            defaults="Check back soon — new packages are released regularly."
          />
        }
      />
    );
  }

  // Category filter derived from the catalog's own package types, mirroring the
  // /book package picker so both surfaces bucket packages identically. Labels
  // resolve through the shared PACKAGE_TYPE_LABEL_KEYS map (business-type aware).
  const uniqueTypes = [...new Set(packages.map((pkg) => pkg.package_type))];
  const typeOptions = [
    { value: 'all', label: tr('filterAll', { defaultValue: 'All' }) },
    ...uniqueTypes.map((type) => ({
      value: type,
      label:
        (PACKAGE_TYPE_LABEL_KEYS[type]
          ? tr(PACKAGE_TYPE_LABEL_KEYS[type])
          : '') ||
        type
          .split('_')
          .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(' '),
    })),
  ];
  const showTypeFilter = uniqueTypes.length > 1;
  const focusedPackage = packages.find((pkg) => pkg.id === focusedPackageId);
  const visiblePackages = focusedPackage
    ? [focusedPackage]
    : selectedType === 'all'
      ? packages
      : packages.filter((pkg) => pkg.package_type === selectedType);

  return (
    <div>
      {promotionUnavailable && (
        <p role="status" className="text-muted-foreground mb-4 text-sm">
          <Trans i18nKey="packages:nativeView.promotionUnavailable" defaults="This offer is no longer available for you here. Browse the available packages below." />
        </p>
      )}
      {focusedPackage && (
        <button
          type="button"
          onClick={() => setFocusedPackageId(undefined)}
          className="mb-4 min-h-11 text-sm underline underline-offset-4"
          data-test="package-promotion-show-all"
        >
          <Trans i18nKey="packages:nativeView.showAllPackages" defaults="View all packages" />
        </button>
      )}
      {showTypeFilter && !focusedPackage && (
        <div
          style={{
            display: 'flex',
            gap: 8,
            overflowX: 'auto',
            paddingBottom: 6,
            marginBottom: 16,
            scrollSnapType: 'x mandatory',
          }}
        >
          {typeOptions.map((opt) => {
            const active = selectedType === opt.value;
            return (
              <NativeFilterChip
                key={opt.value}
                active={active}
                onClick={() => setSelectedType(opt.value)}
              >
                {opt.label}
              </NativeFilterChip>
            );
          })}
        </div>
      )}

      {visiblePackages.map((pkg) => (
        <NativePurchasePackageCard
          key={pkg.id}
          pkg={pkg}
          offers={offersByPackage?.[pkg.id] ?? []}
          onPurchase={onPurchase ? () => onPurchase(pkg) : undefined}
        />
      ))}

      <p
        style={{
          marginTop: 16,
          paddingTop: 20,
          paddingBottom: 12,
          borderTop: `1px solid ${t.line}`,
          fontFamily: t.fontSans,
          fontSize: 11,
          color: t.muted,
          lineHeight: 1.6,
          textAlign: 'center',
        }}
      >
        <Trans
          i18nKey="packages:nativeView.purchaseFinePrint"
          defaults="Credits transfer between sessions until they expire."
        />
      </p>
    </div>
  );
}

function NativePurchasePackageCard({
  pkg,
  offers,
  onPurchase,
}: {
  pkg: AvailablePackage;
  offers: PublicDiscountCode[];
  /** Opens the in-place checkout dialog. Absent = deep-link to /book. */
  onPurchase?: () => void;
}) {
  const t = useNativeTokens();
  const { t: tr, i18n } = useTranslation('bookings');
  const { t: trPkg } = useTranslation('packages');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const localized = selectPackageCopy(
    {
      description: pkg.description,
      description_alt: pkg.description_alt,
      features: pkg.features,
      features_alt: pkg.features_alt,
    },
    i18n.language,
  );
  const previewFeatures = localized.features.slice(
    0,
    PACKAGE_FEATURES_PREVIEW_COUNT,
  );
  const showViewDetails =
    localized.features.length > PACKAGE_FEATURES_PREVIEW_COUNT ||
    (localized.description !== null &&
      localized.description.length > PACKAGE_DESCRIPTION_OVERFLOW_CHARS);

  // Location-scoped packages must carry their venue slug through the
  // deep-link. Both booking surfaces resolve the active location from
  // `?location=<slug>` (falling back to the cookie), and /book/packages
  // hard-filters to `location_id IS NULL` when nothing resolves — so without
  // this a visitor with no cookie lands on a page that filtered out the very
  // package they tapped. Global packages (location_id null) need no param.
  const locationParam = pkg.location_slug
    ? `&location=${encodeURIComponent(pkg.location_slug)}`
    : '';
  const purchaseStyle = {
    width: '100%',
    marginTop: 18,
    padding: '15px 0',
    background: t.accent,
    color: t.background,
    border: 'none',
    borderRadius: t.radius,
    textDecoration: 'none',
    fontFamily: t.fontSans,
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase' as const,
    fontWeight: 500,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    cursor: 'pointer',
  };

  const href = pkg.is_fixed_slot
    ? `/book/packages?package=${pkg.id}${locationParam}`
    : `/book?package=${pkg.id}${locationParam}`;

  const totalSavings =
    pkg.original_price && pkg.original_price > pkg.price
      ? pkg.original_price - pkg.price
      : 0;

  // For bundles the loader has already rolled sibling credits up into
  // pkg.booking_count, so the typed label reads "{Type} Bundle: N Classes".
  const subtitle = formatPackageLabel(
    trPkg,
    pkg.package_type,
    pkg.booking_count,
    pkg.is_bundle,
  );
  const perBookingPrice = Math.round(pkg.price / pkg.booking_count);
  // Per-player price ladder: 1-player row = base price, larger sizes (≤6, the
  // checkout cap) from player_pricing. Per-person = total / size.
  const pricing = pkg.player_pricing;
  const playerTiers = pricing
    ? [
        { size: 1, total: Number(pkg.price) },
        ...Object.keys(pricing)
          .map((k) => Number(k))
          .filter((n) => Number.isInteger(n) && n > 1 && n <= 6)
          .sort((a, b) => a - b)
          .map((n) => ({ size: n, total: Number(pricing[String(n)]) })),
      ]
    : null;
  const validityLabel = (() => {
    // Fixed validity window ("August package") — show the actual date range
    // instead of a duration; credits are only usable inside it.
    if (pkg.fixed_starts_at && pkg.fixed_ends_at) {
      return trPkg('nativeView.validityWindowRange', {
        defaultValue: '{{start}} – {{end}}',
        start: formatLocaleDate(pkg.fixed_starts_at, i18n.language, {
          year: undefined,
        }),
        end: formatLocaleDate(pkg.fixed_ends_at, i18n.language, {
          year: undefined,
        }),
      });
    }
    if (pkg.validity_days) {
      if (pkg.validity_days % 30 === 0) {
        const months = pkg.validity_days / 30;
        return `${months} ${tr('months', { defaultValue: 'months' })}`;
      }
      if (pkg.validity_days % 7 === 0) {
        const weeks = pkg.validity_days / 7;
        return `${weeks} ${tr('weeks', { defaultValue: 'weeks' })}`;
      }
      return `${pkg.validity_days} ${tr('days', { defaultValue: 'days' })}`;
    }
    return `${pkg.validity_months} ${tr('months', { defaultValue: 'months' })}`;
  })();

  return (
    <div
      style={{
        padding: '24px 22px 22px',
        background: t.background,
        border: `1px solid ${t.line}`,
        borderRadius: t.radius,
        marginBottom: 14,
        position: 'relative',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: 6,
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <Eyebrow size="sm" style={{ marginBottom: 6 }}>
            {subtitle}
          </Eyebrow>
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 24,
              fontWeight: 400,
              color: t.foreground,
              lineHeight: 1.1,
            }}
          >
            {pkg.name}
          </div>
          {localized.description && (
            <div
              dir="auto"
              style={{
                fontFamily: t.fontSans,
                fontSize: 12,
                color: t.muted,
                marginTop: 6,
                lineHeight: 1.45,
                display: '-webkit-box',
                WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {localized.description}
            </div>
          )}
        </div>
        {pkg.is_fixed_slot && (
          <span
            style={{
              padding: '5px 10px',
              background: `${t.foreground}10`,
              fontFamily: t.fontMono,
              fontSize: 8,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: t.foreground,
              flexShrink: 0,
            }}
          >
            <Trans i18nKey="packages:customerView.fixedSlotBadge" />
          </span>
        )}
      </div>

      {/* Price block */}
      <div
        style={{
          marginTop: 22,
          paddingTop: 20,
          borderTop: `1px solid ${t.line}`,
        }}
      >
        {pkg.player_pricing && (
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: t.muted,
              marginBottom: 4,
            }}
          >
            <Trans i18nKey="bookings:startingAt" defaults="From" />
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <CurrencyIcon
            style={{
              width: 26,
              height: 26,
              strokeWidth: 1.5,
              color: t.foreground,
            }}
          />
          <div
            style={{
              fontFamily: t.fontDisplay,
              fontSize: 48,
              fontWeight: 300,
              color: t.foreground,
              lineHeight: 0.95,
              letterSpacing: '-0.02em',
            }}
          >
            {Number(pkg.price).toLocaleString()}
          </div>
          {totalSavings > 0 && (
            <div
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 18,
                fontWeight: 300,
                color: t.muted,
                textDecoration: 'line-through',
                lineHeight: 1,
                alignSelf: 'flex-end',
                paddingBottom: 4,
              }}
            >
              {Number(pkg.original_price).toLocaleString()}
            </div>
          )}
        </div>
        {/* Savings + voucher share one wrapping row. Both were inline-flex
            siblings before, so they ran together on a single cramped line with
            no reliable gap. A flex row with wrap gives each its own space and
            lets the voucher drop to its own line on a narrow card. */}
        {(totalSavings > 0 || offers.length > 0) && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 8,
            }}
          >
            {totalSavings > 0 && (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  fontFamily: t.fontSans,
                  fontSize: 11,
                  color: t.muted,
                  letterSpacing: '0.04em',
                }}
              >
                <Trans
                  i18nKey="packages:nativeView.totalSavingsLabel"
                  defaults="Package savings"
                />
                <CurrencyIcon
                  style={{
                    width: 11,
                    height: 11,
                    strokeWidth: 1.5,
                    color: t.muted,
                  }}
                />
                {totalSavings.toLocaleString()}
              </div>
            )}

            <NativePackageOffers price={pkg.price} offers={offers} />
          </div>
        )}
      </div>

      {/* Price by group size — makes the individual-vs-group difference (and the
          cheaper per-head group rate) visible without opening the checkout dialog. */}
      {playerTiers && (
        <div
          style={{
            marginTop: 18,
            paddingTop: 16,
            borderTop: `1px solid ${t.line}`,
          }}
        >
          <div
            style={{
              fontFamily: t.fontMono,
              fontSize: 9,
              letterSpacing: '0.22em',
              textTransform: 'uppercase',
              color: t.muted,
              marginBottom: 10,
            }}
          >
            <Trans
              i18nKey="packages:nativeView.priceByGroupSize"
              defaults="Price by group size"
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {playerTiers.map((tier) => {
              const perPlayer = Math.round(tier.total / tier.size);
              return (
                <div
                  key={tier.size}
                  style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span
                    style={{
                      fontFamily: t.fontSans,
                      fontSize: 13,
                      color: t.foreground,
                    }}
                  >
                    {tier.size === 1
                      ? trPkg('nativeView.onePlayerPrivate', {
                          defaultValue: '1 player (private)',
                        })
                      : trPkg('playersCount', { count: tier.size })}
                  </span>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'baseline',
                      gap: 8,
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontFamily: t.fontSans,
                        fontSize: 14,
                        color: t.foreground,
                      }}
                    >
                      <CurrencyIcon
                        style={{
                          width: 13,
                          height: 13,
                          strokeWidth: 1.5,
                          color: t.foreground,
                        }}
                      />
                      {tier.total.toLocaleString()}
                    </span>
                    {tier.size > 1 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 3,
                          fontFamily: t.fontSans,
                          fontSize: 11,
                          color: t.muted,
                        }}
                      >
                        <CurrencyIcon
                          style={{
                            width: 10,
                            height: 10,
                            strokeWidth: 1.5,
                            color: t.muted,
                          }}
                        />
                        {perPlayer.toLocaleString()}
                        {trPkg('nativeView.perPlayerSuffix', {
                          defaultValue: ' / player',
                        })}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Three-column metric strip */}
      <div
        style={{
          marginTop: 18,
          padding: '12px 0',
          borderTop: `1px solid ${t.line}`,
          borderBottom: `1px solid ${t.line}`,
          display: 'flex',
        }}
      >
        {[
          {
            l: (
              <Trans
                i18nKey="packages:nativeView.metricCredits"
                defaults="Credits"
              />
            ),
            v: pkg.booking_count,
          },
          {
            l: (
              <Trans
                i18nKey="packages:nativeView.metricPerBooking"
                defaults="Per booking"
              />
            ),
            v: (
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <CurrencyIcon
                  style={{
                    width: 13,
                    height: 13,
                    strokeWidth: 1.5,
                    color: t.foreground,
                  }}
                />
                {perBookingPrice}
              </span>
            ),
          },
          {
            l: (
              <Trans
                i18nKey="packages:nativeView.metricValid"
                defaults="Valid"
              />
            ),
            v: validityLabel,
          },
        ].map((s, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              textAlign: 'center',
              borderInlineStart: i ? `1px solid ${t.line}` : 'none',
              padding: '4px 0',
            }}
          >
            <div
              style={{
                fontFamily: t.fontDisplay,
                fontSize: 17,
                fontWeight: 400,
                color: t.foreground,
              }}
            >
              {s.v}
            </div>
            <div
              style={{
                fontFamily: t.fontMono,
                fontSize: 8,
                letterSpacing: '0.2em',
                textTransform: 'uppercase',
                color: t.muted,
                marginTop: 4,
              }}
            >
              {s.l}
            </div>
          </div>
        ))}
      </div>

      {/* Includes list — capped preview, full list lives in the drawer */}
      {previewFeatures.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {previewFeatures.map((line, i) => (
            <div
              key={i}
              dir="auto"
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                padding: '6px 0',
                fontFamily: t.fontSans,
                fontSize: 12,
                color: t.foreground,
              }}
            >
              <span style={{ color: t.foreground, marginTop: 2, opacity: 0.7 }}>
                ✓
              </span>
              <span
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {line}
              </span>
            </div>
          ))}
        </div>
      )}

      {showViewDetails && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setDetailsOpen(true);
          }}
          style={{
            marginTop: 12,
            background: 'transparent',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
            fontFamily: t.fontMono,
            fontSize: 10,
            letterSpacing: '0.2em',
            textTransform: 'uppercase',
            color: t.foreground,
            textDecoration: 'underline',
            textUnderlineOffset: 3,
            opacity: 0.85,
          }}
        >
          {tr('viewDetails')}
        </button>
      )}

      <DetailsDrawer
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
        name={pkg.name}
        description={localized.description}
        features={localized.features}
        subtitle={subtitle}
      />

      {/* Fixed-slot packages still deep-link to /book/packages: they need the
          recurring-cadence flow (weekdays, times, coach) that the checkout
          dialog has no UI for. Everything else opens in place, so cancelling
          returns the customer to this tab instead of stranding them on Book. */}
      {onPurchase && !pkg.is_fixed_slot ? (
        <button type="button" onClick={onPurchase} style={purchaseStyle}>
          <Trans i18nKey="packages:customerView.purchase" />
          <DirectionalArrow size={12} strokeWidth={1.4} />
        </button>
      ) : (
        <Link href={href} style={purchaseStyle}>
          <Trans i18nKey="packages:customerView.purchase" />
          <DirectionalArrow size={12} strokeWidth={1.4} />
        </Link>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Currency symbol — italic display of the tenant ISO code with editorial      */
/* weighting. Renders the active tenant currency from <TenantCurrencyProvider>.*/
/* -------------------------------------------------------------------------- */

function SARSymbol({ size = 14 }: { size?: number }) {
  const t = useNativeTokens();
  const currency = useTenantCurrency();
  return (
    <span
      style={{
        fontFamily: t.fontDisplay,
        fontSize: size,
        fontStyle: 'italic',
        fontWeight: 400,
        color: 'inherit',
        letterSpacing: '0.02em',
        marginInlineEnd: 2,
      }}
    >
      {currency}
    </span>
  );
}

/* -------------------------------------------------------------------------- */
/* Skeletons                                                                  */
/* -------------------------------------------------------------------------- */

function HistorySkeleton() {
  const t = useNativeTokens();
  return (
    <div>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 0.5 } 50% { opacity: 0.3 } }`}</style>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 56,
            borderBottom: `1px solid ${t.line}`,
            backgroundColor: t.line,
            opacity: 0.4,
            animation: 'pulse 1.4s ease-in-out infinite',
          }}
        />
      ))}
    </div>
  );
}
